import React from "react";
import { useTranslation } from "react-i18next";
import CircularProgress from "@mui/material/CircularProgress";
import TableContainer from "@mui/material/TableContainer";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import TableHead from "@mui/material/TableHead";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

const EDITABLE_FIELDS = ["price", "location", "stock", "discount"];

const StorageRow = ({ branch, isEditing, editingItem, onEdit, onDelete, onChangeField, onSave, onCancel }) => {
    const { t } = useTranslation();

    if (isEditing) {
        return (
            <TableRow>
                <TableCell>{branch.warehouseName}</TableCell>
                {EDITABLE_FIELDS.map((field) => (
                    <TableCell key={field}>
                        <TextField
                            size="small"
                            value={editingItem[field] ?? ""}
                            onChange={(e) => onChangeField(field, e.target.value)}
                            sx={{ width: 100 }}
                        />
                    </TableCell>
                ))}
                <TableCell>
                    <Tooltip title={t("common.save")}>
                        <IconButton size="small" color="primary" onClick={onSave}>
                            <CheckIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.cancel")}>
                        <IconButton size="small" onClick={onCancel}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </TableCell>
            </TableRow>
        );
    }

    return (
        <TableRow hover>
            <TableCell>{branch.warehouseName}</TableCell>
            <TableCell>{branch.price}</TableCell>
            <TableCell>{branch.location}</TableCell>
            <TableCell>{branch.stock}</TableCell>
            <TableCell>{branch.discount}</TableCell>
            <TableCell>
                <Tooltip title={t("common.edit")}>
                    <IconButton size="small" onClick={() => onEdit(branch.id)}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title={t("common.delete")}>
                    <IconButton size="small" color="error" onClick={() => onDelete(branch.id)}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </TableCell>
        </TableRow>
    );
};

export const StorageTable = ({
    storageList,
    editingItem,
    loading,
    error,
    onEdit,
    onDelete,
    onEditChange,
    onEditSave,
    onEditCancel,
}) => {
    const { t } = useTranslation();

    return (
        <>
            <Grid size={{ xs: 12 }}>
                <Typography variant="h6">{t("productsAndServices.titleGrid")}</Typography>
            </Grid>

            {error && (
                <Grid size={{ xs: 12 }}>
                    <Alert severity="error">{error}</Alert>
                </Grid>
            )}

            <Grid size={{ xs: 12 }}>
                <TableContainer component={Paper} sx={{ mt: 2, position: "relative" }}>
                    {loading && (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "rgba(255,255,255,0.6)",
                                zIndex: 1,
                            }}
                        >
                            <CircularProgress size={28} />
                        </Box>
                    )}
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t("productsAndServices.store")}</TableCell>
                                <TableCell>{t("crm.products.price")}</TableCell>
                                <TableCell>{t("crm.products.location")}</TableCell>
                                <TableCell>{t("crm.products.stock")}</TableCell>
                                <TableCell>{t("crm.products.discount")}</TableCell>
                                <TableCell>{t("common.actions")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {storageList.length === 0 && !loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            {t("messages.noRecords")}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                storageList.map((branch) => (
                                    <StorageRow
                                        key={branch.id}
                                        branch={branch}
                                        isEditing={editingItem?.id === branch.id}
                                        editingItem={editingItem}
                                        onEdit={onEdit}
                                        onDelete={onDelete}
                                        onChangeField={onEditChange}
                                        onSave={onEditSave}
                                        onCancel={onEditCancel}
                                    />
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
        </>
    );
};